import { useCallback, useState, useRef } from 'react'
import classNames from 'classnames'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form/dist/index.ie11'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faCheck } from '@fortawesome/pro-regular-svg-icons'
import formatError from '../../../utilities/formatError'
import { formatCurrency, formatMonths, formatPercentage, compareRates } from '../utils'
import { Stack } from '../../stack'
import { Button } from '../../button'
import { TextField } from '../../textfield'
import { Alert } from '../../alert'
import { Row, Col } from '../../grid'
import { Heading } from '../../typography/Heading'
import computeOffsetTop from '../../../utilities/computeTopOffset'
import useEnabledOnClient from '../../../utilities/useEnabledOnClient'
import * as styles from '../CalculatorModule.module.scss'

const AutoRefinanceCalculator = () => {
  const [t, i18n] = useTranslation()
  const [language, countryCode] = i18n.language.split('-')
  const [calcResults, setCalcResults] = useState()
  const [storedFormData, setStoredFormData] = useState()
  const disabled = useEnabledOnClient()

  const { register, errors, handleSubmit, getValues } = useForm({
    mode: 'onBlur',
    defaultValues: {
      currentBalance: 0,
      currentRate: 7,
      currentPayment: 0,
      proposedRate: 5,
    },
  })

  const validationSchema = {
    currentBalance: {
      min: 0,
      max: 999999999,
    },
    currentRate: {
      min: 0,
      max: 40,
    },
    currentPayment: {
      min: 0,
      max: 999999999,
    },
    proposedRate: {
      min: 0,
      max: 40,
      validate: value =>
        compareRates(value, getValues('currentRate')) ||
        t('AutoRefinanceCalculator.proposedRateMessage', {
          defaultValue:
            "The 'Proposed interest rate' must be less than or equal to the 'Current interest rate'",
        }),
    },
  }

  const [inProgress, setInProgress] = useState(false)
  const [alert, showAlert] = useState(null)
  const dismissAlert = useCallback(() => showAlert(null), [])
  const resultsContainer = useRef(null)
  const resultsHeader = useRef(null)

  const handleSubmitValid = async formData => {
    setInProgress(true)
    showAlert(false)
    setStoredFormData(formData)

    try {
      const url = 'https://www.calcxml.com/rest/aut07'

      // Documentation
      // https://www.calcxml.com/documentation/rest/Aut07JSONRequest.htm

      const payload = {
        username: process.env.CALCXML_USERNAME,
        password: process.env.CALCXML_PASSWORD,
        language,
        countryCode,
        chartWidth: 900,
        chartHeight: 400,
        barColor1: '63B5E4',
        barColor2: '84BD00',
        barColor3: 'FCC841',
        // barColor4: '#ffffff',
        // barColor5: '#ffffff',
        // barColor6: '#ffffff',
        currentBalance: formData.currentBalance,
        currentRate: formatPercentage(formData.currentRate),
        currentPayment: formData.currentPayment,
        proposedRate: formatPercentage(formData.proposedRate),
      }

      const response = await axios.post(url, payload)

      if (response.status !== 200 || Boolean(response.data.error)) {
        throw new Error(`Unexpected status code: ${response.status} ${response.statusText}`)
      }

      setInProgress(false)
      setCalcResults(response.data)
      window.scrollTo({
        top: computeOffsetTop(resultsContainer.current) - 20,
        behavior: 'smooth',
      })
      resultsHeader.current.focus()
    } catch (error) {
      setInProgress(false)
      showAlert({
        type: 'error',
        title: 'Error',
        message: error.message,
      })
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  return (
    <div>
      {alert && (
        <Row>
          <Col xs={12}>
            <Alert variant={alert.type} title={alert.title} onDismiss={dismissAlert}>
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}
      <form
        action="/"
        method="POST"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleSubmitValid)}
        className={alert ? styles.HasAlert : ''}
      >
        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('AutoRefinanceCalculator.firstLegend', {
              defaultValue: 'Assumptions',
            })}
          </legend>
          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('AutoRefinanceCalculator.currentBalanceLabel', {
                  defaultValue: 'Current loan balance ($)',
                })}
                id="currentBalance"
                name="currentBalance"
                type="number"
                ref={register(validationSchema.currentBalance)}
                error={formatError(errors.currentBalance, validationSchema.currentBalance, t)}
                autoFocus
                autoSelect
              />
            </Col>

            <Col xs={12} lg={6}>
              <TextField
                label={t('AutoRefinanceCalculator.currentRateLabel', {
                  defaultValue: 'Current interest rate (0% to 40%)',
                })}
                id="currentRate"
                name="currentRate"
                type="number"
                min="0"
                max="40"
                ref={register(validationSchema.currentRate)}
                error={formatError(errors.currentRate, validationSchema.currentRate, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('AutoRefinanceCalculator.currentPaymentLabel', {
                  defaultValue: 'Current monthly payment ($)',
                })}
                id="currentPayment"
                name="currentPayment"
                type="number"
                min="0"
                ref={register(validationSchema.currentPayment)}
                error={formatError(errors.currentPayment, validationSchema.currentPayment, t)}
                autoSelect
              />
            </Col>
            <Col xs={12} lg={6}>
              <TextField
                label={t('AutoRefinanceCalculator.proposedRateLabel', {
                  defaultValue: 'Proposed interest rate (0% to 40%)',
                })}
                id="proposedRate"
                name="proposedRate"
                type="number"
                min="0"
                max="40"
                ref={register(validationSchema.proposedRate)}
                error={formatError(errors.proposedRate, validationSchema.proposedRate, t)}
                autoSelect
              />
            </Col>
          </Row>
        </fieldset>

        <Stack alignment="center" className={styles.Submit}>
          <Button
            type="submit"
            disabled={inProgress || disabled}
            loading={inProgress}
            alt={t('AutoRefinanceCalculator.calculateAlt', {
              defaultValue: 'Calculate to view results',
            })}
          >
            {t('AutoRefinanceCalculator.calculate', {
              defaultValue: 'Calculate',
            })}
          </Button>

          {alert && alert.type === 'error' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faTimes} className={styles.TimesIcon} />
              {t('AutoRefinanceCalculator.couldNotCalculate', {
                defaultValue: 'Sorry, could not calculate',
              })}
            </span>
          )}

          {alert && alert.type === 'success' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faCheck} className={styles.CheckIcon} />
              {t('AutoRefinanceCalculator.calculationComplete', {
                defaultValue: 'Calculation complete. See below for results!',
              })}
            </span>
          )}
        </Stack>
      </form>

      <div ref={resultsContainer} />

      {calcResults && (
        <div className={styles.Results}>
          <Heading
            className={styles.ResultsHeading}
            element="h3"
            size={3}
            tabIndex="0"
            forwardedRef={resultsHeader}
          >
            {t('AutoRefinanceCalculator.results', {
              defaultValue: 'Results',
            })}
          </Heading>
          <p>{calcResults.responseText}</p>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('AutoRefinanceCalculator.summaryTable', {
              defaultValue: 'Summary table',
            })}
          </Heading>

          <table className={styles.ResultsTable} role="grid">
            <caption>
              {t('AutoRefinanceCalculator.summaryTable', {
                defaultValue: 'Summary table',
              })}
            </caption>
            <tbody>
              <tr>
                <th aria-label="No value" />
                <th scope="col">
                  {t('AutoRefinanceCalculator.rate', {
                    defaultValue: 'Rate',
                  })}
                </th>
                <th scope="col">
                  {t('AutoRefinanceCalculator.payment', {
                    defaultValue: 'Payment',
                  })}
                </th>
                <th scope="col">
                  {t('AutoRefinanceCalculator.monthsRemaining', {
                    defaultValue: 'Months remaining',
                  })}
                </th>
                <th scope="col">
                  {t('AutoRefinanceCalculator.interestRemaining', {
                    defaultValue: 'Interest remaining',
                  })}
                </th>
              </tr>
              <tr>
                <th>
                  {t('AutoRefinanceCalculator.current', {
                    defaultValue: 'Current',
                  })}
                </th>
                <td>{storedFormData.currentRate}%</td>
                <td>{formatCurrency(storedFormData.currentPayment)}</td>
                <td>{formatMonths(calcResults.currentMonths)}</td>
                <td>{formatCurrency(calcResults.currentInterest)}</td>
              </tr>
              <tr>
                <th>
                  {t('AutoRefinanceCalculator.proposed', {
                    defaultValue: 'Proposed',
                  })}
                </th>
                <td>{storedFormData.proposedRate}%</td>
                <td>{formatCurrency(calcResults.proposedPayment)}</td>
                <td>{formatMonths(calcResults.proposedMonths)}</td>
                <td>{formatCurrency(calcResults.proposedInterest)}</td>
              </tr>
            </tbody>
          </table>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('AutoRefinanceCalculator.graph', {
              defaultValue: 'Graph',
            })}
          </Heading>
          <img
            src={calcResults.chartUrl}
            alt={t('AutoRefinanceCalculator.graphOfResults', {
              defaultValue: 'Graph of results',
            })}
          />
        </div>
      )}
    </div>
  )
}

export { AutoRefinanceCalculator }
export default AutoRefinanceCalculator
